/**
 * PATH: packages/document-editor/src/serialization.ts
 * FILE_ID: DOCEDITOR-SERIALIZATION-001
 * PURPOSE: Safe HTML compatibility around the versioned JSON source of truth.
 * WHY: Legacy HTML remains importable while persisted state stays deterministic.
 * RELATED: contracts.ts, extensions.ts
 */
import { Editor, type AnyExtension } from '@tiptap/core'
import {
  createDocument,
  isSafeDocumentUrl,
  validateDocument,
  type DocumentMetadata,
  type DocumentValidationOptions,
  type VersionedDocument,
} from './contracts'
import { buildDocumentExtensions } from './extensions'

const BLOCKED_TAGS = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button',
  'textarea', 'select', 'option', 'video', 'audio', 'canvas', 'svg', 'math',
])

export function sanitizeDocumentHtml(input: string): string {
  if (typeof DOMParser === 'undefined') {
    throw new Error('HTML import requires a browser-compatible DOMParser')
  }
  const parsed = new DOMParser().parseFromString(input, 'text/html')
  parsed.body.querySelectorAll('*').forEach((element) => {
    if (BLOCKED_TAGS.has(element.tagName.toLowerCase())) {
      element.remove()
      return
    }
    for (const attr of Array.from(element.attributes)) {
      const name = attr.name.toLowerCase()
      const value = attr.value.trim()
      if (name.startsWith('on') || name === 'srcdoc') element.removeAttribute(attr.name)
      if ((name === 'href' || name === 'src') && !isSafeDocumentUrl(value, name === 'src')) {
        element.removeAttribute(attr.name)
      }
      if (name === 'style' && /(expression\s*\(|url\s*\(\s*['"]?\s*javascript:)/i.test(value)) {
        element.removeAttribute(attr.name)
      }
    }
  })
  return parsed.body.innerHTML
}

function editorFor(
  content: string | Record<string, unknown>,
  extensions?: AnyExtension[],
): Editor {
  return new Editor({
    extensions: extensions ?? buildDocumentExtensions(),
    content,
    editable: false,
  })
}

export interface HtmlImportOptions {
  metadata?: Partial<DocumentMetadata>
  extensions?: AnyExtension[]
  validation?: DocumentValidationOptions
}

export function importDocumentHtml(
  html: string,
  options: HtmlImportOptions = {},
): VersionedDocument {
  const editor = editorFor(sanitizeDocumentHtml(html), options.extensions)
  try {
    const document = createDocument(editor.getJSON(), options.metadata)
    const result = validateDocument(document, options.validation)
    if (!result.valid) throw new Error(`Invalid imported document: ${result.errors.join('; ')}`)
    return document
  } finally {
    editor.destroy()
  }
}

export interface HtmlExportOptions {
  extensions?: AnyExtension[]
  validation?: DocumentValidationOptions
}

export function exportDocumentHtml(
  document: VersionedDocument,
  options: HtmlExportOptions = {},
): string {
  const result = validateDocument(document, options.validation)
  if (!result.valid) throw new Error(`Cannot render invalid document: ${result.errors.join('; ')}`)
  const editor = editorFor(document.content as Record<string, unknown>, options.extensions)
  try {
    return sanitizeDocumentHtml(editor.getHTML())
  } finally {
    editor.destroy()
  }
}

export function serializeDocument(document: VersionedDocument): string {
  const result = validateDocument(document)
  if (!result.valid) throw new Error(`Cannot serialize invalid document: ${result.errors.join('; ')}`)
  return JSON.stringify(document)
}

export function deserializeDocument(json: string): VersionedDocument {
  const document = JSON.parse(json) as unknown
  const result = validateDocument(document)
  if (!result.valid) throw new Error(`Invalid document JSON: ${result.errors.join('; ')}`)
  return document as VersionedDocument
}
